<template>
  <div ref="detailsView" :class="['detail-wrapper', { 'is-mobile': isMobile, 'is-open': isOpen }]">
    <div class="detail-view">
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { isMobile } from '@/core/helpers/ui.helper';
import { useNavigationStore } from '@/stores/navigation.store';
import { useTableStore } from '@/stores/table.store';
import type { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { FlowDirections } from '@/views/composables/constants/gam.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';

export interface DetailViewProps {
  listId: GamListId;
  isOpen?: boolean;
  isInverse?: boolean;
}

const props = withDefaults(defineProps<DetailViewProps>(), {});

const navigationStore = useNavigationStore();
const tableStore = useTableStore(props.listId)();
const { detailsOpen, selectedIndex, itemList, tableParams } = storeToRefs(tableStore);

const detailsView = ref<HTMLElement>();
const lockKey = ref<boolean>(false);
const emits = defineEmits([GamComponentsEmits.CLOSE]);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const changeItem = async (direction?: FlowDirections | MouseEvent): Promise<void> => {
  if (!direction) detailsOpen.value = false;
  const listLength = itemList.value?.length || 0;

  if (listLength === 0) return;

  if (direction === FlowDirections.PREVIOUS && selectedIndex.value - 1 >= 0) {
    tableStore.selectItem(selectedIndex.value - 1);
  } else if (itemList.value && direction === FlowDirections.NEXT && selectedIndex.value + 1 < listLength) {
    tableStore.selectItem(selectedIndex.value + 1);
  } else if (selectedIndex.value + 1 === listLength && tableParams.value.afterCursor) {
    lockKey.value = true;
    await tableStore.infiniteLoad();
    tableStore.selectItem(selectedIndex.value + 1);
    lockKey.value = false;
  } else {
    emits(GamComponentsEmits.CLOSE);
  }

  navigationStore.setSwipeAware(true);
};

watch(selectedIndex, (index) => {
  const listLength = itemList.value?.length || 0;
  lockKey.value = index + 1 === listLength;
});

// TODO: Replace with @vueuse keypress

// useKeypress({
//   keyEvent: KeyEvent.KEY_DOWN,
//   keyBinds: [
//     {
//       keyCode: Key.DOWN,
//       success: () => {
//         const itemsLength = itemList.value?.length || 0;
//         const isDisabled = selectedIndex.value + 1 >= itemsLength || itemsLength === 1 || lockKey.value;
//         if (!isDisabled) changeItem(FlowDirections.NEXT);
//       },
//     },
//     {
//       keyCode: Key.UP,
//       success: () => {
//         const isDisabled = selectedIndex.value !== 0;
//         if (!isDisabled) changeItem(FlowDirections.PREVIOUS);
//       },
//     },
//   ],
// });
</script>

<style lang="scss" scoped>
.detail-wrapper.is-mobile {
  padding-left: 0;
  padding-right: 0;
}

.help-wrapper {
  opacity: 1;
}

.help-wrapper.is-swipe-aware {
  opacity: 0;
  transition: opacity 2s linear;
}

.scrollable-area {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-view .detail-wrapper .detail-view {
  max-width: unset;
  padding: unset;
}

.detail-header {
  position: absolute;
  width: 100%;
  padding: var(--spacing-xxl) var(--spacing-xxl) var(--spacing-menu);
  max-width: calc(596px + var(--spacing-xxl) * 2);
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  pointer-events: none;

  .gam-button {
    pointer-events: auto;
  }
}
</style>
